import apiEndpoints from '../../const/apiEndpoints'
import api from '../../services'
import { createAsyncThunk } from '@reduxjs/toolkit'
import {
  Institute,
  GetBatchesPayload,
  GetAdminPayload,
  GetBranchesPayload,
  GetAdminResponse,
  Batch,
  Branch,
  GetBranchListResponse,
  GetBranchListPayload,
  Course,
  WebexUser,
  GetInstituteListResponse,
  AdditionalCourse,
  EditInstitutePayload
} from './types'
import ROUTES from '../../const/routes'
import history from '../../const/history'

export const getInstitutes = createAsyncThunk(
  'coachingCenter/getInstitutes',
  async (): Promise<Array<Institute>> => {
    const data = {
      ascDesc: 'Desc',
      pageNo: '1',
      pageSize: '100',
      searchCriteria: {},
      sortBy: 'createdAt'
    }
    const response = await api.post(`${apiEndpoints.getInstitutes}`, data)
    return response?.data?.content
  }
)

export const getInstituteList = createAsyncThunk(
  'coachingCenter/getInstituteList',
  async (pageNo?: number): Promise<GetInstituteListResponse> => {
    const data = {
      ascDesc: 'Desc',
      pageNo: pageNo || 1,
      pageSize: '10',
      searchCriteria: {},
      sortBy: 'createdAt'
    }
    const {
      data: {
        content: instituteList,
        pageable: { pageNumber },
        totalElements,
        totalPages
      }
    } = await api.post(`${apiEndpoints.getInstitutes}`, data)
    const responseData = {
      instituteList,
      pageNumber: pageNumber + 1,
      totalElements,
      totalPages
    }
    return responseData
  }
)

export const getAdditionalCourses = createAsyncThunk(
  'coachingCenter/getAdditionalCourses',
  async (coachingCentreId: string): Promise<Array<AdditionalCourse>> => {
    const response = await api.get(
      `${apiEndpoints.getAdditionalCourses}?coachingCentreId=${coachingCentreId}`
    )
    return response?.data
  }
)

export const getBranches = createAsyncThunk(
  'coachingCenter/getBranches',
  async (
    { coachingCentreId, type }: GetBranchesPayload,
    { dispatch }
  ): Promise<Array<Branch>> => {
    const response = await api.get(
      `${apiEndpoints.getBranches}?coachingCentreId=${coachingCentreId}`
    )
    type && dispatch(getAdminList({ coachingCentreId, type }))
    return response?.data
  }
)

export const getBatches = createAsyncThunk(
  'coachingCenter/getBatches',
  async (
    { coachingCentreId, branchId, type }: GetBatchesPayload,
    { dispatch }
  ): Promise<Array<Batch>> => {
    const response = await api.get(
      `${apiEndpoints.getBatches}?coachingCentreId=${coachingCentreId}&branchIds=${branchId}`
    )
    type && dispatch(getAdminList({ coachingCentreId, branchId, type }))
    return response?.data
  }
)

export const getBranchList = createAsyncThunk(
  'coachingCenter/getBranchList',
  async ({
    coachingCentreId,
    pageNo
  }: GetBranchListPayload): Promise<GetBranchListResponse> => {
    const data = {
      ascDesc: 'Desc',
      pageNo,
      pageSize: '10',
      searchCriteria: {
        ...(coachingCentreId && { coachingCenterId: coachingCentreId })
      },
      sortBy: 'createdAt'
    }
    const {
      data: {
        content: branchList,
        pageable: { pageNumber },
        totalElements,
        totalPages
      }
    } = await api.post(`${apiEndpoints.getBranchList}`, data)
    const responseData = {
      branchList,
      pageNumber: pageNumber + 1,
      totalElements,
      totalPages
    }
    return responseData
  }
)

export const getAvailableCourses = createAsyncThunk(
  'coachingCenter/getAvailableCourses',
  async (coachingCentreId: string): Promise<Array<Course>> => {
    const response = await api.get(
      `${apiEndpoints.getAvailableCourses}?coachingCentreId=${coachingCentreId}`
    )
    return response?.data
  }
)

export const getBranchById = createAsyncThunk(
  'coachingCenter/getBranchById',
  async (branchId: string): Promise<Branch> => {
    const { data } = await api.get(`${apiEndpoints.getBranchById}/${branchId}`)
    return data?.data
  }
)

export const getInstituteById = createAsyncThunk(
  'coachingCenter/getInstituteById',
  async (instituteId: string): Promise<Institute> => {
    const { data } = await api.get(
      `${apiEndpoints.getInstituteById}${instituteId}`
    )
    return data?.data
  }
)

export const getWebexUsers = createAsyncThunk(
  'coachingCenter/getWebexUsers',
  async (): Promise<Array<WebexUser>> => {
    const response = await api.get(apiEndpoints.getWebexUsers)
    return response?.data
  }
)

export const getAdminList = createAsyncThunk(
  'coachingCenter/getAdminList',
  async ({
    coachingCentreId,
    branchId,
    batchId,
    pageNo,
    type
  }: GetAdminPayload): Promise<GetAdminResponse> => {
    const payloadData = {
      ascDesc: 'Desc',
      batchIds: batchId ? [batchId] : null,
      branchIds: branchId ? [branchId] : null,
      coachingCenterId: coachingCentreId || null,
      pageNo: pageNo || 1,
      pageSize: 20,
      searchCriteria: { userType: type },
      sortBy: 'created_at'
    }

    const {
      data: { data: adminList, page, pageSize, totalCount, totalPages }
    } = await api.post(`${apiEndpoints.getAdminList}`, payloadData)
    const responseData = {
      adminList,
      page,
      pageSize,
      totalCount,
      totalPages,
      coachingCentreId: coachingCentreId || '',
      branchId: branchId || '',
      batchId: batchId || ''
    }
    return responseData
  }
)

export const saveBranch = createAsyncThunk(
  'coachingCenter/saveBranch',
  async (requestPayload: Branch): Promise<Branch> => {
    const { data } = await api.post(
      `${apiEndpoints.saveBranch}`,
      requestPayload
    )

    if (data) {
      history.push(ROUTES.BRANCH_LIST)
    }
    return data
  }
)

export const editBranch = createAsyncThunk(
  'coachingCenter/editBranch',
  async (requestPayload: Branch): Promise<Branch> => {
    const { data } = await api.put(
      `${apiEndpoints.coachingCentreBranch}${requestPayload?.id}`,
      requestPayload
    )

    if (data) {
      history.push(ROUTES.BRANCH_LIST)
    }
    return data
  }
)

export const deleteBranch = createAsyncThunk(
  'coachingCenter/deleteBranch',
  async (coachingCentreId: string, { dispatch }): Promise<Array<Branch>> => {
    const response = await api.delete(
      `${apiEndpoints.coachingCentreBranch}${coachingCentreId}`
    )
    dispatch(getBranchList({ pageNo: 1 }))
    return response?.data
  }
)

export const deleteInstitute = createAsyncThunk(
  'coachingCenter/deleteInstitute',
  async (coachingCentreId: string, { dispatch }): Promise<Array<Branch>> => {
    const response = await api.delete(
      `${apiEndpoints.deleteInstitute}${coachingCentreId}`
    )
    dispatch(getInstituteList())
    return response?.data
  }
)

export const getCourses = createAsyncThunk(
  'coachingCenter/getCourses',
  async (): Promise<Array<Course>> => {
    const response = await api.get(apiEndpoints.getCourses)
    return response?.data
  }
)

export const getCoachingCenter = createAsyncThunk(
  'coachingCenter/getCoachingCenter',
  async (): Promise<Array<Institute>> => {
    const payload = {
      pageNo: '1',
      pageSize: '100',
      sortBy: 'createdAt',
      ascDesc: 'Desc',
      searchCriteria: {}
    }
    const response = await api.post(apiEndpoints.getCoachingCenter, payload)
    return response?.data.content
  }
)

export const editInstitute = createAsyncThunk(
  'coachingCenter/edit',
  async (
    { requestPayload, coachingCentreId }: EditInstitutePayload,
    { dispatch }
  ): Promise<Institute> => {
    const headers = {
      'content-type':
        'multipart/form-data; boundary=----WebKitFormBoundary7MA4YWxkTrZu0gW'
    }
    const { data } = await api.put(
      `${apiEndpoints.editInstitute}${coachingCentreId}`,
      requestPayload,
      { headers }
    )

    if (data) {
      dispatch(getInstituteList())
      history.goBack()
    }
    return data
  }
)

export const addInstitute = createAsyncThunk(
  'coachingCenter/add',
  async (requestPayload: FormData, { dispatch }): Promise<Institute> => {
    const headers = {
      'content-type':
        'multipart/form-data; boundary=----WebKitFormBoundary7MA4YWxkTrZu0gW'
    }
    const { data } = await api.post(apiEndpoints.addInstitute, requestPayload, {
      headers
    })

    if (data) {
      dispatch(getInstituteList())
      history.goBack()
    }
    return data
  }
)
